/**
 * Plan | Faktisk (#201): the week read in planned time or in clocked time, so drift shows before payroll.
 * Plan is the planned pair for every shift; Faktisk is the clocked pair, a shift still running counted up to
 * now, and a shift nobody clocked has no time at all. Pure, so the page and its tests share one rule.
 */
import type { ApiShift } from '@/api/data';
import { oslo } from '@/rules';

export type TMode = 'plan' | 'fakt';
export const TMODE_KEY = 'vaktplan.tmode';

export const readTMode = (): TMode => {
  try {
    return localStorage.getItem(TMODE_KEY) === 'fakt' ? 'fakt' : 'plan';
  } catch {
    return 'plan';
  }
};
export const writeTMode = (m: TMode): void => {
  try {
    localStorage.setItem(TMODE_KEY, m);
  } catch {}
};

/**
 * The shift as the mode counts it, for hours and cost: the planned pair with no punches (Plan), or the
 * clocked pair as both the plan and the punch (Faktisk), a running one ending now. Null: not clocked.
 */
export function asMode(s: ApiShift, mode: TMode, now: number = Date.now()): ApiShift | null {
  if (mode === 'plan')
    return { ...s, punch_in_time: null, punch_out_time: null, lunch_in_time: null, lunch_out_time: null };
  if (!s.punch_in_time) return null;
  const out = s.punch_out_time ?? new Date(Math.max(now, Date.parse(s.punch_in_time))).toISOString();
  return { ...s, start_time: s.punch_in_time, end_time: out, punch_out_time: out };
}

/** What a Faktisk card says: the clocked time, still on the job, not clocked though it began, or not yet. */
export function factState(
  s: ApiShift,
  now: number = Date.now(),
): 'clocked' | 'running' | 'missing' | 'ahead' {
  if (s.punch_in_time) return s.punch_out_time ? 'clocked' : 'running';
  return Date.parse(s.start_time) > now ? 'ahead' : 'missing';
}

/** The planned pair as the card prints it, «08:00–16:00». */
export const planText = (s: ApiShift): string => `${oslo(s.start_time)?.time}–${oslo(s.end_time)?.time}`;

/**
 * Clocked against planned length in minutes, for a shift clocked in and out; null under 15 minutes either
 * way (the prototype's threshold), and for a shift with no full punch pair.
 */
export function driftMin(s: ApiShift): number | null {
  if (!s.punch_in_time || !s.punch_out_time) return null;
  const clocked = Date.parse(s.punch_out_time) - Date.parse(s.punch_in_time);
  const planned = Date.parse(s.end_time) - Date.parse(s.start_time);
  if (!Number.isFinite(clocked) || !Number.isFinite(planned)) return null;
  const dm = Math.round((clocked - planned) / 60000);
  return Math.abs(dm) >= 15 ? dm : null;
}

/** «+40 min», «−20 min», an hour or more in hours with one decimal, «+1,3 t». */
export function driftText(dm: number): string {
  const a = Math.abs(dm);
  const sign = dm > 0 ? '+' : '−';
  return a >= 60 ? `${sign}${String(Math.round(a / 6) / 10).replace('.', ',')} t` : `${sign}${a} min`;
}
